import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Group, Text } from '@mantine/core';
import { IconEraser } from '@tabler/icons-react';

interface SignaturePadProps {
  /** Chizilganda PNG data URL, tozalanganda null qaytaradi. */
  onChange: (dataUrl: string | null) => void;
  height?: number;
  disabled?: boolean;
  error?: string | null;
}

const STROKE = '#16182b';

/**
 * Onlayn imzo maydoni — tashqi kutubxonasiz, oddiy canvas.
 * Sichqoncha, barmoq va stylus (Pointer Events) bilan ishlaydi; `touch-action: none`
 * tufayli mobil brauzerda chizayotganda sahifa siljimaydi.
 * Rasm SHAFFOF fonda saqlanadi — PNG hajmi kichik va hujjatga toza joylashadi.
 */
export function SignaturePad({ onChange, height = 170, disabled, error }: SignaturePadProps) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const dirtyRef = useRef(false);
  const [empty, setEmpty] = useState(true);
  // onChange har renderda yangilanishi mumkin — global listener'ni qayta ulamaslik uchun ref
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /** Canvas o'lchamini konteynerga moslaydi (chizilganini yo'qotmasdan). */
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    const box = boxRef.current;
    if (!canvas || !box) return;
    const cssW = Math.max(240, Math.floor(box.clientWidth));
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(cssW * dpr);
    const h = Math.round(height * dpr);
    if (canvas.width === w && canvas.height === h) return;

    const prev = dirtyRef.current && canvas.width > 0 ? canvas.toDataURL('image/png') : null;
    canvas.width = w;
    canvas.height = h;
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // o'lcham o'zgarganda kontekst tiklanadi
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = STROKE;
    ctx.fillStyle = STROKE;
    ctxRef.current = ctx;

    if (prev) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, cssW, height);
      img.src = prev;
    }
  }, [height]);

  useEffect(() => {
    fit();
    // Klaviatura ochilishi / ekran burilishi — o'lcham o'zgarsa moslaymiz
    const box = boxRef.current;
    if (typeof ResizeObserver === 'undefined' || !box) {
      window.addEventListener('resize', fit);
      return () => window.removeEventListener('resize', fit);
    }
    const ro = new ResizeObserver(() => fit());
    ro.observe(box);
    return () => ro.disconnect();
  }, [fit]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const handleDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* eski brauzer — ahamiyatsiz */
    }
    drawing.current = true;
    const p = point(e);
    last.current = p;
    // Bitta bosish ham nuqta qoldirsin
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2);
    ctx.fill();
    dirtyRef.current = true;
    if (empty) setEmpty(false);
  };

  const handleMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = ctxRef.current;
    const from = last.current;
    if (!ctx || !from) return;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  /** Chiziqni yakunlaydi va natijani (PNG) beradi. */
  const endStroke = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChangeRef.current(canvas.toDataURL('image/png'));
  }, []);

  // Barmoq/sichqoncha canvas tashqarisida qo'yib yuborilsa ham chiziq tugasin
  useEffect(() => {
    window.addEventListener('pointerup', endStroke);
    window.addEventListener('pointercancel', endStroke);
    return () => {
      window.removeEventListener('pointerup', endStroke);
      window.removeEventListener('pointercancel', endStroke);
    };
  }, [endStroke]);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    dirtyRef.current = false;
    setEmpty(true);
    onChange(null);
  };

  return (
    <Box ref={boxRef}>
      <Group justify="space-between" mb={6} wrap="nowrap">
        <Text size="sm" fw={600}>
          Imzo <Text span c="red">*</Text>
        </Text>
        <Button size="compact-xs" variant="subtle" color="gray" leftSection={<IconEraser size={14} />} onClick={clear} disabled={disabled || empty}>
          Tozalash
        </Button>
      </Group>

      <Box
        style={{
          position: 'relative',
          border: `1px ${error ? 'solid var(--mantine-color-red-5)' : 'dashed var(--mantine-color-gray-4)'}`,
          borderRadius: 12,
          background: 'var(--mantine-color-gray-0)',
          overflow: 'hidden',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handleDown}
          onPointerMove={handleMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          style={{ display: 'block', width: '100%', height, touchAction: 'none', cursor: disabled ? 'not-allowed' : 'crosshair' }}
        />
        {empty && (
          <Text
            size="sm"
            c="dimmed"
            style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
          >
            Shu yerga barmoq yoki sichqoncha bilan imzo chizing
          </Text>
        )}
      </Box>

      {error ? (
        <Text size="xs" c="red" mt={4}>{error}</Text>
      ) : (
        <Text size="xs" c="dimmed" mt={4}>Imzoingiz shartnoma hujjatiga qo'yiladi.</Text>
      )}
    </Box>
  );
}
